import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  CardActions
} from '@mui/material';
import {
  PlayArrow,
  Assessment,
  TrendingUp,
  History,
  Star
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { useAuth } from '../App';

const StudentDashboard = () => {
  const [results, setResults] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [requesting, setRequesting] = useState({});
  const [info, setInfo] = useState('');
  const [joinStatusByTest, setJoinStatusByTest] = useState({});
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [resultsRes, testsRes] = await Promise.allSettled([
        axios.get('/api/tests/results'),
        axios.get('/api/tests')
      ]);

      if (resultsRes.status === 'fulfilled') {
        setResults(resultsRes.value.data.results || []);
      }
      if (testsRes.status === 'fulfilled') {
        const list = testsRes.value.data.tests || [];
        setTests(list);
        // Preload join statuses for each test
        try {
          const results = await Promise.allSettled(list.map(t => axios.get(`/api/tests/${t.id}`)));
          const map = {};
          results.forEach((res, idx) => {
            if (res.status === 'fulfilled') {
              const s = res.value?.data?.test?.joinStatus || null;
              map[list[idx].id] = s;
            }
          });
          setJoinStatusByTest(map);
        } catch (_) { /* ignore */ }
      }

      if (resultsRes.status === 'rejected' && testsRes.status === 'rejected') {
        setError('Failed to load dashboard data');
      }
    } catch (error) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartOrRequest = async (testId) => {
    setInfo('');
    setError('');
    try {
      const { data } = await axios.get(`/api/tests/${testId}`);
      const joinStatus = data?.test?.joinStatus || null;
      if (joinStatus === 'approved') {
        navigate(`/tests/${testId}`);
        return;
      }
      if (joinStatus === 'pending') {
        setInfo('Your join request is pending approval.');
        setJoinStatusByTest(prev => ({ ...prev, [testId]: 'pending' }));
        return;
      }
      setRequesting(prev => ({ ...prev, [testId]: true }));
      await axios.post(`/api/tests/${testId}/join-requests`);
      setInfo('Join request sent. Please wait for admin approval.');
      setJoinStatusByTest(prev => ({ ...prev, [testId]: 'pending' }));
    } catch (e) {
      const msg = e?.response?.data?.error || 'Failed to process join request';
      setError(msg);
    } finally {
      setRequesting(prev => ({ ...prev, [testId]: false }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Categories removed

  // Calculate statistics
  const totalTests = results.length;
  const averageScore = totalTests > 0
    ? Math.round(results.reduce((sum, result) => sum + result.score, 0) / totalTests)
    : 0;
  const bestScore = totalTests > 0
    ? Math.max(...results.map(result => result.score))
    : 0;

  // Prepare chart data (last 10 tests)
  const chartData = results
    .slice(-10)
    .map((result, index) => ({
      test: `Test ${index + 1}`,
      score: result.score,
      date: formatDate(result.submittedAt)
    }));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  const StatCard = ({ title, value, icon, color = 'primary' }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="h2">
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}.main`,
              borderRadius: '50%',
              width: 56,
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white'
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Welcome back, {user?.name}!
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Ready to test your knowledge? Choose a test to get started.
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Tests Taken"
            value={totalTests}
            icon={<Assessment />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Average Score"
            value={`${averageScore}%`}
            icon={<TrendingUp />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Best Score"
            value={`${bestScore}%`}
            icon={<Star />}
            color="warning"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Published Tests */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" gutterBottom>
                Available Tests
              </Typography>
              <Box>
                <Button onClick={() => navigate('/test')} startIcon={<PlayArrow />} sx={{ mr: 1 }}>Take Test</Button>
                <Button onClick={() => navigate('/results')} startIcon={<History />}>My Results</Button>
              </Box>
            </Box>
            {tests.length > 0 ? (
              <Grid container spacing={2}>
                {tests.map(test => (
                  <Grid item xs={12} key={test.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {test.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {test.description || 'No description'}
                        </Typography>
                        {joinStatusByTest[test.id] === 'pending' && (
                          <Box sx={{ mt: 1 }}>
                            <Chip size="small" color="warning" label="Waiting for approval" />
                          </Box>
                        )}
                      </CardContent>
                      <CardActions>
                        {joinStatusByTest[test.id] === 'approved' ? (
                          <Button size="small" startIcon={<PlayArrow />} onClick={() => navigate(`/tests/${test.id}`)}>
                            Start Test
                          </Button>
                        ) : joinStatusByTest[test.id] === 'pending' ? (
                          <Button size="small" disabled>
                            Waiting for approval
                          </Button>
                        ) : (
                          <Button size="small" startIcon={<PlayArrow />} onClick={() => handleStartOrRequest(test.id)} disabled={!!requesting[test.id]}>
                            {requesting[test.id] ? 'Sending...' : 'Request Access'}
                          </Button>
                        )}
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                <Typography color="text.secondary">No tests available</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Test Categories removed */}

        {/* Performance Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Performance Trend
            </Typography>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="test" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="#1976d2"
                    strokeWidth={2}
                    dot={{ fill: '#1976d2', strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={250}>
                <Typography color="text.secondary">Take some tests to see your performance trend</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Recent Results */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Recent Test Results
              </Typography>
              <Button
                startIcon={<History />}
                onClick={() => navigate('/results')}
              >
                View All Results
              </Button>
            </Box>
            {results.length > 0 ? (
              <List>
                {results.slice(0, 5).map((result, index) => (
                  <ListItem
                    key={result.id}
                    divider={index < Math.min(results.length, 5) - 1}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <ListItemText primary={formatDate(result.submittedAt)} />
                    <Box display="flex" alignItems="center" gap={2}>
                      <Chip
                        label={`${result.score}%`}
                        color={result.score >= 80 ? 'success' : result.score >= 60 ? 'warning' : 'error'}
                        variant="filled"
                      />
                      <Typography variant="body2" color="text.secondary">
                        {result.correctAnswers}/{result.totalQuestions}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                <Typography color="text.secondary">No test results yet. Take your first test!</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
      {info && (
        <Alert severity="info" sx={{ mt: 2 }}>{info}</Alert>
      )}
    </Container>
  );
};

export default StudentDashboard; 